import onBlokkliEvent from "./composables/onBlokkliEvent.js";
import { computed, ref } from "#imports";
import {
  findElement,
  buildDraggableItem,
  falsy,
  modulo,
  onlyUnique
} from "#blokkli/helpers";
import { eventBus } from "#blokkli/helpers/eventBus";
export default function(dom) {
  const selectedUuids = ref([]);
  const activeFieldKey = ref("");
  const draggingMode = ref(null);
  const editableActive = ref(false);
  const isChangingOptions = ref(false);
  const isMultiSelecting = ref(false);
  const interactionMode = ref("mouse");
  const dragItems = ref([]);
  const dragItemsBundles = computed(
    () => dragItems.value.map((v) => v.itemBundle).filter(falsy)
  );
  const uuidsSet = computed(() => new Set(selectedUuids.value));
  const isDragging = computed(() => !!draggingMode.value);
  const blocks = computed(
    () => selectedUuids.value.map((uuid) => {
      return dom.findBlock(uuid);
    }).filter(falsy)
  );
  function selectItems(uuids) {
    unselectItems();
    const items = uuids.map((uuid) => {
      const element = findElement(uuid);
      if (element) {
        const item = buildDraggableItem(element);
        if (item && item.itemType === "existing") {
          return item;
        }
      }
    }).filter(falsy);
    selectedUuids.value = items.map((v) => v.uuid);
  }
  function unselectItems() {
    activeFieldKey.value = "";
    if (selectedUuids.value.length === 0) {
      return;
    }
    selectedUuids.value = [];
  }
  function onSelect(v) {
    if (typeof v === "string") {
      selectItems([v]);
    } else {
      selectItems(v.filter(onlyUnique));
    }
  }
  const selectInList = (prev) => {
    const items = dom.getAllBlocks();
    if (!items.length) {
      return;
    }
    const currentIndex = blocks.value[0] ? items.findIndex((v) => v.uuid === blocks.value[0].uuid) : -1;
    const targetIndex = modulo(
      prev ? currentIndex - 1 : currentIndex + 1,
      items.length
    );
    const targetItem = items[targetIndex];
    if (!targetItem) {
      return;
    }
    onSelect(targetItem.uuid);
    dom.refreshBlockRect(targetItem.uuid);
    eventBus.emit("scrollIntoView", { uuid: targetItem.uuid });
  };
  const setActiveFieldKey = (key) => activeFieldKey.value = key;
  onBlokkliEvent("select", onSelect);
  onBlokkliEvent("select:start", (e) => {
    selectedUuids.value = (e.uuids || []).filter(onlyUnique);
    isMultiSelecting.value = true;
    interactionMode.value = e.mode;
    activeFieldKey.value = "";
  });
  onBlokkliEvent("select:toggle", (uuid) => {
    if (selectedUuids.value.includes(uuid)) {
      selectedUuids.value = selectedUuids.value.filter((v) => v !== uuid);
    } else {
      selectedUuids.value.push(uuid);
    }
  });
  onBlokkliEvent("select:end", (uuids) => {
    isMultiSelecting.value = false;
    if (!uuids || uuids.length === 0 && selectedUuids.value.length === 0) {
      return;
    }
    selectItems(uuids);
  });
  onBlokkliEvent("select:previous", () => selectInList(true));
  onBlokkliEvent("select:next", selectInList);
  onBlokkliEvent("setActiveFieldKey", setActiveFieldKey);
  onBlokkliEvent("state:reloaded", () => {
    selectedUuids.value = selectedUuids.value.filter((uuid) => {
      return !!dom.findBlock(uuid);
    });
  });
  onBlokkliEvent("dragging:start", (e) => {
    draggingMode.value = e.mode;
    isMultiSelecting.value = false;
    dragItems.value = e.items;
    const blocks2 = e.items.filter(
      (v) => v.itemType === "existing"
    );
    if (blocks2.length) {
      selectItems(blocks2.map((v) => v.uuid));
    }
  });
  onBlokkliEvent("dragging:end", () => {
    draggingMode.value = null;
  });
  onBlokkliEvent("select:unselect", () => {
    selectedUuids.value = [];
  });
  onBlokkliEvent("window:clickAway", () => {
    unselectItems();
    activeFieldKey.value = "";
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });
  const isDraggingExisting = computed(() => {
    return isDragging.value && !!dragItems.value.length && dragItems.value.some(
      (v) => v.itemType === "existing" || v.itemType === "existing_structure"
    );
  });
  function isBlockSelected(uuid) {
    return uuidsSet.value.has(uuid);
  }
  return {
    uuids: selectedUuids,
    blocks,
    activeFieldKey,
    isDragging,
    isDraggingExisting,
    setActiveFieldKey,
    editableActive,
    isChangingOptions,
    isMultiSelecting,
    draggingMode,
    interactionMode,
    dragItems,
    uuidsSet,
    dragItemsBundles,
    isBlockSelected
  };
}
